// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { toast } from 'sonner';
import { ConnectionStatus, type ConnectionStatusType } from '../../lib/useWebSocket';
import { type GameStatusEnum } from '../../types/game';
import { getStatusDisplayText } from '../../utils/helpers';
import { Button } from '../Button';

interface GameBoardHeaderProps {
  currentRound: number;
  currentStatus: GameStatusEnum;
  wsStatus: ConnectionStatusType;
  isJudge: boolean;
  roomCode: string | null;
  sessionId: string | null;
  showMagicCode: boolean;
  onToggleMagicCode: () => void;
  onLeaveGame: () => void;
}

export function GameBoardHeader({
  currentRound,
  currentStatus,
  wsStatus,
  isJudge,
  roomCode,
  sessionId,
  showMagicCode,
  onToggleMagicCode,
  onLeaveGame,
}: GameBoardHeaderProps) {
  return (
    <>
      {/* Magic Code Banner - Copyable */}
      {showMagicCode && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-1 text-center px-2">
          <button
            onClick={() => {
              navigator.clipboard.writeText(roomCode || sessionId || '');
              toast.success('Room code copied!');
            }}
            className="font-mono text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-wider text-black hover:text-gray-800 transition-colors cursor-pointer truncate max-w-full"
            title="Click to copy room code"
            data-testid="game-room-code"
          >
            {roomCode || sessionId}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="glass border-b border-white/20 p-3 sm:p-4">
        <div className="container mx-auto max-w-7xl flex items-center justify-between gap-2 sm:gap-4">
          <div className="min-w-0">
            <h1
              className="font-display text-2xl sm:text-3xl font-bold"
              data-testid="game-round-number"
            >
              Round {currentRound}
            </h1>
            <p className="text-xs sm:text-sm text-gray-300" data-testid="game-phase-indicator">
              {getStatusDisplayText(currentStatus)}
            </p>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4 flex-shrink-0">
            <div
              className={`h-3 w-3 rounded-full ${
                wsStatus === ConnectionStatus.CONNECTED ? 'bg-green-500' : 'bg-red-500'
              }`}
              data-testid="game-connection-indicator"
              title={wsStatus === ConnectionStatus.CONNECTED ? 'Connected' : 'Disconnected'}
            />
            <span className="text-xs sm:text-sm hidden sm:inline" data-testid="game-role-indicator">
              {isJudge ? '🎭 Psychiatrist' : '🏥 Patient'}
            </span>
            <Button
              variant="secondary"
              onClick={onToggleMagicCode}
              className="text-xs sm:text-xs px-2 sm:px-3 py-1 sm:py-2 min-h-9 min-w-9 sm:min-h-8 sm:min-w-8"
              title={showMagicCode ? 'Hide room code' : 'Show room code'}
              data-testid="game-toggle-code-button"
            >
              🪄
            </Button>
            <Button
              variant="secondary"
              onClick={onLeaveGame}
              className="text-xs sm:text-xs px-2 sm:px-3 py-1 sm:py-2 min-h-9 min-w-9 sm:min-h-8 sm:min-w-8"
              data-testid="game-leave-button"
            >
              Leave
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
